import clsx from 'clsx'
import { memo, useMemo } from 'react'
import type { UseTabsProps } from '../../../../hooks/useTabs'

interface TabNavProps {
  activeIndex: number
  tabRoutes: UseTabsProps
}

const TabNav: React.FC<TabNavProps> = memo(({ activeIndex, tabRoutes }) => {
  const visibleItems = useMemo(() => {
    const last = tabRoutes.length - 1

    const indices = new Set<number>()

    const add = (v: number | number[]) => {
      if (Array.isArray(v)) {
        v.forEach(n => indices.add(n))
      } else {
        indices.add(v)
      }
    }

    // Always show first and last
    add([0, last])

    if (activeIndex <= 1) {
      // Beginning: 0–1–2–3
      add([0, 1, 2, 3])
    } else if (activeIndex === 2) {
      // Middle transition: 2–3–4
      add([2, 3, 4])
    } else {
      // From index 3 onward: 3..last
      for (let i = 3; i <= last; i++) add(i)
    }

    const sorted = [...indices].sort((a, b) => a - b)

    const result: Array<{
      label: string
      path: string
      index: number
      isEllipsis?: boolean
    }> = []

    sorted.forEach((current, i) => {
      const prev = sorted[i - 1]

      if (i > 0 && current - prev > 1) {
        result.push({
          label: '...',
          path: '',
          index: -1,
          isEllipsis: true,
        })
      }

      result.push({
        ...tabRoutes[current],
        index: current,
      })
    })

    return result
  }, [tabRoutes, activeIndex])
  return (
    <div
      className={clsx(
        'z-10 flex flex-row mb-6 opacity-70',
        'static min-1100:fixed min-1100:left-8 min-1100:top-1/2 top-4',
        'min-1100:transform min-1100:-translate-y-1/2',
        'min-1100:flex-col min-1100:gap-4',
      )}
    >
      {/* Desktop: show all tabs */}
      <div className="hidden min-1100:flex min-1100:flex-col min-1100:gap-4">
        {tabRoutes.map(({ label, path }, index) => {
          const isCompleted = index < activeIndex
          const isPending = index > activeIndex

          return (
            <div key={path} className="flex items-center gap-2">
              <button
                data-testid={`tab-nav-${path.replace('/', '')}-btn`}
                className={clsx(
                  'rounded-full cursor-default h-6 w-6 flex items-center justify-center',
                  'border border-white',
                  {
                    'bg-white text-black': index <= activeIndex,
                    'text-white': index > activeIndex,
                    'opacity-40': isPending,
                    'opacity-100': !isPending,
                  },
                )}
              >
                {isCompleted ? <i className="fa-solid fa-check" /> : index + 1}
              </button>
              <span className="text-white whitespace-nowrap text-xs">
                {label}
              </span>
            </div>
          )
        })}
      </div>

      {/* Mobile: show paginated tabs */}
      <div className="flex flex-row min-1100:hidden">
        {visibleItems.map((item, idx) => {
          if (item.isEllipsis) {
            return (
              <div
                key={`ellipsis-${idx}`}
                className="flex items-center justify-center w-6 h-6 mx-1 text-white opacity-40 -mt-1"
              >
                ...
              </div>
            )
          }

          const index = item.index
          const isActive = index === activeIndex
          const isCompleted = index < activeIndex
          const isPending = index > activeIndex

          const nextItem = visibleItems[idx + 1]
          const showSeparator =
            idx !== visibleItems.length - 1 && !nextItem?.isEllipsis

          return (
            <div key={item.path} className="flex items-center">
              <button
                data-testid={`tab-nav-${item.path.replace('/', '')}-btn`}
                className={clsx(
                  'rounded-full cursor-default h-6 w-6 flex items-center justify-center',
                  'border border-white',
                  {
                    'bg-white text-black': index <= activeIndex,
                    'text-white': index > activeIndex,
                    'opacity-40': isPending,
                    'opacity-100': !isPending,
                  },
                )}
              >
                {isCompleted ? <i className="fa-solid fa-check" /> : index + 1}
              </button>

              {isActive && (
                <span className="text-white whitespace-nowrap text-xs ms-2">
                  {item.label}
                </span>
              )}

              {showSeparator && (
                <div className="w-2 h-px mx-2 bg-white opacity-40" />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
})

export { TabNav }
